import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface Favorite {
  number: string
  name: string
}

const FAVORITES_KEY = ['phone-favorites'] as const

/**
 * Favoris du téléphone, persistés côté serveur (PAR AGENT). Remplace l'ancien
 * store Zustand/localStorage. Expose la même interface (favorites, isFavorite,
 * toggleFavorite) pour un remplacement transparent des consommateurs.
 */
export function useFavorites() {
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: FAVORITES_KEY,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await api.get<{ favorites: Favorite[] }>(
        '/api/agents/favorites/',
      )
      return data.favorites || []
    },
  })

  const favorites = query.data ?? []
  const favNumbers = new Set(favorites.map((f) => f.number))

  const addMut = useMutation({
    mutationFn: async (fav: Favorite) => {
      await api.post('/api/agents/favorites/', {
        number: fav.number,
        name: fav.name,
      })
      return fav
    },
    // Optimiste : l'étoile bascule tout de suite.
    onMutate: async (fav) => {
      await qc.cancelQueries({ queryKey: FAVORITES_KEY })
      const prev = qc.getQueryData<Favorite[]>(FAVORITES_KEY) ?? []
      qc.setQueryData<Favorite[]>(FAVORITES_KEY, [
        ...prev.filter((f) => f.number !== fav.number),
        fav,
      ])
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(FAVORITES_KEY, ctx.prev)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: FAVORITES_KEY }),
  })

  const removeMut = useMutation({
    mutationFn: async (number: string) => {
      await api.delete('/api/agents/favorites/', { data: { number } })
      return number
    },
    onMutate: async (number) => {
      await qc.cancelQueries({ queryKey: FAVORITES_KEY })
      const prev = qc.getQueryData<Favorite[]>(FAVORITES_KEY) ?? []
      qc.setQueryData<Favorite[]>(
        FAVORITES_KEY,
        prev.filter((f) => f.number !== number),
      )
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(FAVORITES_KEY, ctx.prev)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: FAVORITES_KEY }),
  })

  const isFavorite = (number: string) => favNumbers.has(number)

  const toggleFavorite = (number: string, name: string) => {
    if (favNumbers.has(number)) removeMut.mutate(number)
    else addMut.mutate({ number, name })
  }

  return {
    favorites,
    isFavorite,
    toggleFavorite,
    isLoading: query.isLoading,
  }
}
